import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import invoiceService from '../../services/invoiceService';

const CompanyInvoiceManagement = () => {
    // Data
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    // Users for filter dropdown (company-scoped)
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);

    // Other filters
    const [globalFilter, setGlobalFilter] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const [downloadingInvoices, setDownloadingInvoices] = useState({});
    const [error, setError] = useState(null);

    // Metrics
    const [metrics, setMetrics] = useState({
        thisMonth: 0,
        thisQuarter: 0,
        halfYear: 0,
        thisYear: 0,
    });

    const toast = useRef(null);

    // Mount — load users + metrics (ONCE)
    useEffect(() => {
        loadUsers();
        loadMetrics();
    }, []);

    // Reload invoices when pagination changes. Filter changes are user-triggered via Apply.
    useEffect(() => {
        loadInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [first, rows]);

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await invoiceService.getCompanyUsersForFilter({
                limit: 100,
                order: 'name_asc',
            });
            if (response.success) setUsers(response.data || []);
        } catch (err) {
            console.error('Error loading users:', err?.message || 'Unknown error');
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadMetrics = async () => {
        try {
            const response = await invoiceService.getCompanyInvoiceMetrics();
            if (response.success) {
                setMetrics({
                    thisMonth: response.data.this_month || 0,
                    thisQuarter: response.data.this_quarter || 0,
                    halfYear: response.data.half_year || 0,
                    thisYear: response.data.this_year || 0,
                });
            }
        } catch (err) {
            console.error('Error loading metrics:', err?.message || 'Unknown error');
        }
    };

    const loadInvoices = async () => {
        setLoading(true);
        setError(null);
        try {
            const page = Math.floor(first / rows) + 1;
            const formatDate = (date) => {
                if (!date) return undefined;
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                return `${y}-${m}-${d}`;
            };

            const params = {
                page,
                limit: rows,
                sort_field: 'invoice_date',
                sort_order: 'desc',
                user_id: selectedUser || undefined,
                search: globalFilter || undefined,
                date_from: formatDate(dateFrom),
                date_to: formatDate(dateTo),
            };
            Object.keys(params).forEach((k) => {
                if (params[k] === null || params[k] === undefined) delete params[k];
            });

            const response = await invoiceService.getCompanyInvoices(params);
            if (response.data) {
                setInvoices(response.data);
                setTotalRecords(response.total || 0);
            } else {
                setInvoices([]);
                setTotalRecords(0);
            }
        } catch (err) {
            console.error('Error loading invoices:', err?.message || 'Unknown error');
            setError('Failed to load invoices. Please try again.');
            setInvoices([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = () => {
        setFirst(0);
        loadInvoices();
    };

    const clearFilters = () => {
        setGlobalFilter('');
        setDateFrom(null);
        setDateTo(null);
        setSelectedUser(null);
        setFirst(0);
        setTimeout(() => loadInvoices(), 0);
    };

    const handleDownloadInvoice = async (invoice) => {
        setDownloadingInvoices((prev) => ({ ...prev, [invoice.id]: true }));
        try {
            await invoiceService.companyDownloadInvoice(invoice.id, invoice.invoice_number);
        } catch (err) {
            console.error('Download error:', err?.message || 'Unknown error');
            toast.current?.show({
                severity: 'error',
                summary: 'Download failed',
                detail: err?.message || 'Could not download invoice',
                life: 5000,
            });
        } finally {
            setDownloadingInvoices((prev) => ({ ...prev, [invoice.id]: false }));
        }
    };

    // KPI card (P15)
    const KpiCard = ({ icon, label, value, description }) => (
        <div className="col-6 lg:col-3">
            <Card className="h-full shadow-1" pt={{ body: { className: 'p-3' } }}>
                <div className="flex justify-content-between align-items-start">
                    <div className="flex flex-column gap-1">
                        <span className="text-600 text-sm font-medium">{label}</span>
                        <span className="text-900 text-xl font-semibold">{value}</span>
                    </div>
                    <div className="flex align-items-center justify-content-center border-round bg-primary-50 p-2">
                        <i className={`pi ${icon} text-xl text-primary`} aria-hidden="true"></i>
                    </div>
                </div>
                <span className="text-500 text-xs block mt-2">{description}</span>
            </Card>
        </div>
    );

    // Column templates
    const invoiceNumberTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-semibold text-primary">{rowData.invoice_number}</span>
            <small className="text-600">{invoiceService.formatDate(rowData.invoice_date)}</small>
        </div>
    );

    const userTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-medium">{rowData.user?.name || '—'}</span>
            <small className="text-600">{rowData.user?.email || '—'}</small>
        </div>
    );

    const amountTemplate = (rowData) => (
        <div className="text-right">
            <div className="font-semibold text-900">
                {invoiceService.formatCurrency(rowData.total_amount)}
            </div>
            <small className="text-600">
                {rowData.gst_type === 'intra_state' ? 'CGST+SGST' : 'IGST'}
            </small>
        </div>
    );

    const statusTemplate = (rowData) => (
        <Tag
            value={rowData.status?.toUpperCase()}
            severity={invoiceService.getStatusSeverity(rowData.status)}
        />
    );

    const actionTemplate = (rowData) => (
        <div className="flex flex-wrap justify-content-center gap-1">
            <Button
                icon="pi pi-download"
                severity="info"
                text
                size="small"
                tooltip="Download Invoice"
                tooltipOptions={{ position: 'top' }}
                aria-label={`Download invoice ${rowData.invoice_number}`}
                onClick={() => handleDownloadInvoice(rowData)}
                loading={downloadingInvoices[rowData.id] || false}
                disabled={downloadingInvoices[rowData.id] || false}
            />
        </div>
    );

    const userItemTemplate = (option) => (
        <div className="flex flex-column">
            <span className="font-medium">{option.name}</span>
            <small className="text-500">{option.email}</small>
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header */}
                    <div className="mb-4">
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Invoice Management</h1>
                        <p className="text-600 text-sm m-0">
                            View and download invoices for your company employees
                        </p>
                    </div>

                    {/* KPI cards — 2 per row on mobile, 4 per row on desktop */}
                    <div className="grid mb-3">
                        <KpiCard
                            icon="pi-calendar"
                            label="This Month"
                            value={metrics.thisMonth}
                            description="Invoices this month"
                        />
                        <KpiCard
                            icon="pi-chart-bar"
                            label="This Quarter"
                            value={metrics.thisQuarter}
                            description="Current quarter invoices"
                        />
                        <KpiCard
                            icon="pi-chart-line"
                            label="Half Year"
                            value={metrics.halfYear}
                            description="Last 6 months"
                        />
                        <KpiCard
                            icon="pi-file"
                            label="This Year"
                            value={metrics.thisYear}
                            description="Total this year"
                        />
                    </div>

                    {error && (
                        <Message severity="error" text={error} className="w-full mb-3" />
                    )}

                    <Card>
                        {/* Filter bar — tinted, 4 filters + action block in 5-slot grid (P7) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={selectedUser}
                                        options={users}
                                        onChange={(e) => setSelectedUser(e.value)}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name,email"
                                        placeholder="All Users"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                        loading={loadingUsers}
                                        emptyMessage="No users found"
                                        itemTemplate={userItemTemplate}
                                        aria-label="Filter by user"
                                    />
                                </div>

                                <div className="col-6 md:col">
                                    <Calendar
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="From Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        maxDate={dateTo || new Date()}
                                        aria-label="Filter from date"
                                    />
                                </div>

                                <div className="col-6 md:col">
                                    <Calendar
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.value)}
                                        dateFormat="dd/mm/yy"
                                        placeholder="To Date"
                                        showIcon
                                        iconDisplay="input"
                                        className="w-full p-inputtext-sm"
                                        minDate={dateFrom}
                                        maxDate={new Date()}
                                        aria-label="Filter to date"
                                    />
                                </div>

                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={globalFilter}
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFilterChange()}
                                            placeholder="Invoice #"
                                            className="w-full p-inputtext-sm"
                                            aria-label="Search by invoice number"
                                        />
                                    </IconField>
                                </div>

                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        onClick={clearFilters}
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        aria-label="Clear filters"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        label="Apply"
                                        icon="pi pi-check"
                                        onClick={handleFilterChange}
                                        size="small"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <DataTable
                            value={invoices}
                            lazy
                            loading={loading}
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={(e) => {
                                setFirst(e.first);
                                setRows(e.rows);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            dataKey="id"
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-inbox pi-2x text-400 mb-3" />
                                    <span className="text-600">No invoices found.</span>
                                </div>
                            }
                        >
                            <Column
                                field="invoice_number"
                                header="Invoice"
                                body={invoiceNumberTemplate}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="user.name"
                                header="User"
                                body={userTemplate}
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="total_amount"
                                header="Amount"
                                body={amountTemplate}
                                sortable
                                style={{ minWidth: '150px' }}
                            />
                            <Column
                                field="status"
                                header="Status"
                                body={statusTemplate}
                                sortable
                                style={{ minWidth: '120px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionTemplate}
                                style={{ minWidth: '100px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default CompanyInvoiceManagement;
